import React, { useRef, useEffect, useState } from 'react';
import { 
  Box, 
  Button,
  TextField,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Paper,
  IconButton,
  useTheme
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { 
  FilePenLine, 
  Copy, 
  ArrowDownToLine, 
  RefreshCw,
  X
} from 'lucide-react';
import MarkdownRenderer from './MarkdownRenderer';
import { TailSpin } from 'react-loader-spinner';

const MobileEdit = ({ 
  summary,
  draftSummary,
  setDraftSummary,
  onSave,
  selectedPrompt,
  setSelectedPrompt,
  promptTemplates,
  onCopy,
  onDownload,
  onRegenerate,
  onClose,
  conversation,
  generatingSummary
}) => {
  const { t } = useTranslation();
  const textFieldRef = useRef(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const theme = useTheme();
  
  // Focus the text field when editing starts
  useEffect(() => {
    if (isEditing && textFieldRef.current) {
      textFieldRef.current.focus();
    }
  }, [isEditing]);
  
  // Auto-resize the text field as content changes
  useEffect(() => {
    if (isEditing && textFieldRef.current) {
      textFieldRef.current.style.height = 'auto'; // Reset height
      textFieldRef.current.style.height = `${textFieldRef.current.scrollHeight}px`; // Adjust to content
    }
  }, [draftSummary, isEditing]);

  // Reset regenerating state when generatingSummary changes
  useEffect(() => {
    if (!generatingSummary && isRegenerating) {
      setIsRegenerating(false);
    }
  }, [generatingSummary, isRegenerating]);

  const handleStartEditing = () => {
    setIsEditing(true);
    setDraftSummary(summary);
  };

  const handleSave = () => {
    onSave(conversation.id, selectedPrompt);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setDraftSummary(summary);
    setIsEditing(false);
  };
  
  const handleRegenerate = () => {
    setIsRegenerating(true);
    onRegenerate(conversation.id, selectedPrompt);
  };

  const isApproved = conversation.status === 'APPROVED';
  
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        position: 'relative'
      }}
    >
      {/*//! Editing toolbar */}
      {isEditing && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2,
            padding: 2,
            borderBottom: '1px solid #e0e0e0',
            backgroundColor: 'background.paper',
            position: 'sticky',
            top: 0,
            zIndex: 10
          }}
        >
          <Button 
            variant="cancelButton" 
            onClick={handleCancel}
            sx={{ width: '100%', fontSize: 14 }}
          >
            {t('common.cancel')}
          </Button>
          
          <Button 
            variant="saveButton" 
            onClick={handleSave}
            sx={{ width: '100%', fontSize: 14 }}
          >
            {t('common.save')}
          </Button>
        </Box>
      )}
      
      {/* Content area */}
      <Box
        sx={{
          flexGrow: 1,
          padding: 2,
          paddingBottom: '140px', // Reduced padding since we're saving space
          backgroundColor: 'white',
          overflowY: 'auto',
          overflowX: 'hidden', // Prevent horizontal scrolling
          width: '100%'
        }}
      >
        {isEditing ? (
          <TextField
            value={draftSummary || ''}
            onChange={(e) => setDraftSummary(e.target.value)}
            multiline
            fullWidth
            variant="outlined"
            inputRef={textFieldRef}
            sx={{
              '& .MuiOutlinedInput-root': {
                padding: 2,
                backgroundColor: 'white',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.secondary.main,
                borderWidth: 2
              },
              '& .MuiInputBase-input': {
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                hyphens: 'auto'
              }
            }}
            placeholder={t('summary.pressEscToCancel')}
          />
        ) : (
          <Paper
            elevation={0}
            sx={{
              fontFamily: 'Geist Mono',
              p: 1,
              borderRadius: 1,
              backgroundColor: 'white',
              wordWrap: 'break-word',
              overflowWrap: 'break-word',
              hyphens: 'auto',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: isRegenerating ? 'center' : 'stretch',
              justifyContent: isRegenerating ? 'center' : 'flex-start',
              minHeight: '70vh'
            }}
          >
            {isRegenerating ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 8
                }}
              >
                <TailSpin
                  height="40"
                  width="40"
                  color={theme.palette.secondary.main}
                />
                <Typography sx={{ mt: 4 }}>
                  {t('summary.generating')}
                </Typography>
              </Box>
            ) : summary ? (
              <MarkdownRenderer content={summary} />
            ) : (
              <Typography variant="body1">
                {t('summary.noSummaryAvailable')}
              </Typography>
            )}
          </Paper>
        )}
      </Box>
      
      {/* Bottom toolbar */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: 'background.paper',
          borderTopLeftRadius: '12px',
          borderTopRightRadius: '12px',
          padding: 2.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5,
          zIndex: 10,
          boxShadow: '0px -5px 16px 0px rgba(0, 0, 0, 0.15)',
        }}
      >
        {/* Action buttons with close button */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'center' }}>
          <Button
            variant="outlined"
            startIcon={<FilePenLine size={18} strokeWidth={1.5} />}
            onClick={handleStartEditing}
            disabled={isEditing || isRegenerating || isApproved}
            sx={{ 
              flexGrow: 1,
            }}
          >
            {t('common.edit')}
          </Button>
          
          <Button
            variant="outlined"
            startIcon={<Copy size={18} strokeWidth={1.5} />}
            onClick={onCopy}
            disabled={isEditing || isRegenerating || !summary}
            sx={{ 
              flexGrow: 1,
            }}
          >
            {t('common.copy')}
          </Button>
          
          <Button
            variant="outlined"
            startIcon={<ArrowDownToLine size={18} strokeWidth={1.5} />}
            onClick={onDownload}
            disabled={isEditing || isRegenerating || !summary}
            sx={{ 
              flexGrow: 1,
            }}
          >
            {t('common.pdf')}
          </Button>
          
          <IconButton onClick={onClose} size="small" sx={{ ml: 0.5 }}>
            <X size={24} />
          </IconButton>
        </Box>
        
        {/* Dropdown and regenerate button in one line */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl sx={{ flexGrow: 1 }} size="small">
            <Select
              value={selectedPrompt}
              onChange={(e) => setSelectedPrompt(e.target.value)}
              sx={{
                height: 40,
                fontSize: 14,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#E4E4E7',
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 300,
                  },
                },
              }}
              disabled={isEditing || isRegenerating || isApproved}
            >
              {promptTemplates && promptTemplates.map((template) => (
                <MenuItem key={template.id} value={template.id}>
                  {template.template_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <Button
            variant="buttonStandard"
            startIcon={<RefreshCw size={18} strokeWidth={1.5} />}
            onClick={handleRegenerate}
            disabled={isEditing || isRegenerating || isApproved}
            sx={{
              height: 40,
              fontSize: 14,
              whiteSpace: 'nowrap',
              minWidth: 'auto',
              px: 2,
            }}
          >
            {t('summary.regenerateSummary')}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default MobileEdit; 